import "./localization"

import { BrowserRouter, useNavigate } from "react-router-dom"
import { QueryProvider } from "./providers"
import { RouterSetup } from "./router"
import { Toaster } from "sonner"
import { ThemeProvider } from "@/components/theme-provider"
import { PreloadProvider } from "./providers/preload-provider"
import { ErrorBoundary } from "react-error-boundary"
import { Sentry } from "@/entities/sentry"
import { ModalRoot } from "@/widgets"
import { ErrorPage } from "@/pages"
import { useAppStateStore } from "@/entities/app-state/store"
import { Progress } from "@/components/ui/progress"
import { NavigationService } from "@/entities/navigation-service"
import { useEffect } from "react"

export const App = () => {
  return (
    <QueryProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </QueryProvider>
  )
}

const AppAbsoluteElements = () => {
  const isAppLoading = useAppStateStore(s => s.isLoading)

  return (
    <>
      {isAppLoading && (
        <div className="fixed top-0 left-0 z-50 h-4 w-full">
          <Progress indeterminate />
        </div>
      )}
    </>
  )
}

export const AppInner = () => {
  const navigate = useNavigate()

  useEffect(() => {
    NavigationService.setNavigation(navigate)
  }, [])

  return (
    <ErrorBoundary
      onError={(error, info) =>
        Sentry.captureException(error, {
          extra: { componentStack: info.componentStack },
        })
      }
      fallbackRender={({ error }) => (
        <ErrorPage
          errorMessage={error.message ? error.message : "Unknown error"}
        />
      )}
    >
      <ThemeProvider defaultTheme="system" storageKey="theme-light-dark">
        <PreloadProvider>
          <AppAbsoluteElements />
          <RouterSetup />
          <Toaster
            position="bottom-right"
            toastOptions={{
              classNames: {
                toast: "bg-card border-border shadow-lg rounded-lg",
                title: "text-foreground font-medium",
                description: "text-muted-foreground",
                success:
                  "!bg-success/10 !border-success/30 !text-success [&_[data-title]]:!text-success",
                error:
                  "!bg-destructive/10 !border-destructive/30 !text-destructive [&_[data-title]]:!text-destructive",
                warning:
                  "!bg-warning/10 !border-warning/30 !text-warning-foreground [&_[data-title]]:!text-warning-foreground",
                info: "!bg-info/10 !border-info/30 !text-info [&_[data-title]]:!text-info",
              },
              duration: 4000,
            }}
            closeButton
          />
          <ModalRoot />
        </PreloadProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
